import { Ionicons } from "@expo/vector-icons";
import Text from "@/components/shared/AppText";
import * as Linking from "expo-linking";
import moment from "moment";
import React, { useState } from "react";
import {
  View,
  Image,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
} from "react-native";
import { useNavigation } from "@react-navigation/native";
import { useSafeAreaFrame } from "react-native-safe-area-context";
import Toast from "react-native-toast-message";
import tw from "twrnc";

import { brandColor } from "../globalStyle";
import { eventTitle, formatEventDate, minDesktopWidth } from "../globalUtils";
import { LiveIndicator, TestIndicator } from "../shared/Buttons";
import { unlockWithCredit } from "../http/events";
import {
  getEventExpiryMoment,
  isEventArchived,
  shouldShowStorageExpiry,
} from "./eventStorageUtils";

import { useEventAuth } from "../teams/useWorkspaceAuth";
import { useTeamAccess } from "../teams/useWorkspace";
import { teamsErrorMessage } from "../teams/errors";
import { notify } from "./AppToast";
import RoomMenu from "../rooms/RoomMenu";
import { t, tn } from "@/localization";

const BtnBuyUnlock = ({ text, onPress, loading }) => (
  <TouchableOpacity
    onPress={onPress}
    style={[
      tw`h-[42px] flex-row items-center justify-center rounded`,
      { backgroundColor: brandColor },
    ]}
  >
    {loading && (
      <ActivityIndicator style={tw`ml-5 mr-1 mt-0.5`} color="#ffffff" />
    )}
    <Text style={tw`px-5 text-white text-base`}>{text}</Text>
  </TouchableOpacity>
);

const EventCard = React.memo(
  ({ onEventSettingPressed, onGoLivePressed, onAddRoom, now, event }) => {
    const {
      event_cover_path,
      title,
      is_published,
      is_test,
      event_datetime_utc,
      event_timezone,
      event_window_start_datetime_utc,
      event_window_end_datetime_utc,
      slug,
      stream_key,
      broadcaster,
      event_status,
      days,
      days_paid,
    } = event;

    // Teams: the plan and credits of the event's account; what your role
    // there lets you do (everything in your own account).
    const {
      credits,
      refetchProfile,
      subscription_access_expiry_datetime,
      account_type,
    } = useEventAuth(event);
    const team = useTeamAccess();
    const memberView = team.isShared && broadcaster !== team.self;
    const navigation = useNavigation();

    const date = formatEventDate(event_datetime_utc, event_timezone);

    const { width, height } = useSafeAreaFrame();
    const minWidth = Math.min(width, height);
    const isMobileUX = minDesktopWidth > width;

    const liveButtonTitle = is_test
      ? t("Start free test")
      : event_status === "upcoming"
      ? t("Go Live")
      : t("Continue");

    const active = now.isBetween(
      event_window_start_datetime_utc,
      event_window_end_datetime_utc
    );

    const w = isMobileUX ? minWidth - 52 : 380;
    const daysToPay = days - days_paid;
    // Members unlock only as admins, with the account's credits.
    const needToBuy =
      !is_published &&
      !is_test &&
      (!memberView || team.can("spendCredits", event));
    const hasEnoughCredits =
      credits >= daysToPay ||
      subscription_access_expiry_datetime > moment().utc().format();
    const live = event_status === "live";
    // Rooms (components/rooms) are listed on their event's card.
    const rooms = event.rooms || [];
    const groupLive = live || rooms.some((r) => r.event_status === "live");
    const needContinue =
      active && is_published && !live && team.can("goLive", event);
    const archived = isEventArchived(event, account_type, now);
    const expiryMoment = getEventExpiryMoment(event);
    const daysUntilExpiry = expiryMoment
      ? Math.ceil(expiryMoment.diff(now, "days", true))
      : null;
    // Storage purchases are the account holder's.
    const showStorageCtas =
      account_type !== "business" && team.can("buy", event);
    const showExpiryCountdown =
      showStorageCtas &&
      shouldShowStorageExpiry(event, now) &&
      !archived &&
      Number.isFinite(daysUntilExpiry) &&
      daysUntilExpiry > 0 &&
      daysUntilExpiry < 30;
    const showUnarchiveCta = showStorageCtas && archived;
    const extendLinkUrl =
      broadcaster && slug
        ? `https://evt.live/api/upgrade/${broadcaster}/${slug}`
        : null;
    const unarchiveUrl = stream_key
      ? `https://evt.live/api/payRestore/${stream_key}`
      : null;
    const showExtendCta = showExpiryCountdown && !!extendLinkUrl;

    const [loadingUnlock, setloadingUnlock] = useState(false);

    const openExternalUrl = (targetUrl) => {
      if (!targetUrl) return;
      if (Platform.OS === "web" && typeof window !== "undefined") {
        window.open(targetUrl, "_blank", "noopener,noreferrer");
        return;
      }
      Linking.openURL(targetUrl);
    };

    return (
      <TouchableOpacity onPress={() => onEventSettingPressed(event)}>
        <View style={[tw`mb-5`, { width: w, alignSelf: "center" }]}>
          <View style={tw`relative`}>
            <Image
              style={[
                tw`rounded-[5px] overflow-hidden border border-gray-200`,
                {
                  width: w,
                  height: (w * 720) / 1280,
                },
              ]}
              blurRadius={showUnarchiveCta ? 8 : 0}
              width={w}
              height={(w * 720) / 1280}
              source={{ uri: event_cover_path }}
            />
            {rooms.length > 0 && (
              <RoomMenu
                primary={event}
                rooms={rooms}
                onSelect={(room) => onEventSettingPressed(room)}
                onAdd={onAddRoom ? () => onAddRoom(event) : undefined}
              />
            )}
          </View>

          <View style={tw`px-0 py-2.5 flex-row justify-between items-start`}>
            <View style={tw`flex-1 mr-4`}>
              <Text style={tw`font-extrabold text-base`}>
                {eventTitle(event)}
              </Text>
              <Text
                style={tw`text-black text-xs`}
                ellipsizeMode={"tail"}
                numberOfLines={1}
              >
                {date}
              </Text>
              {showExpiryCountdown && (
                <Text style={tw`mt-1 text-xs font-semibold text-gray-700`}>
                  {tn(
                    "Expires in {count} day",
                    "Expires in {count} days",
                    daysUntilExpiry
                  )}
                </Text>
              )}
              {showUnarchiveCta && (
                <Text style={tw`mt-1 text-xs font-semibold text-gray-700`}>
                  {t("Event is in the Secure Archive")}
                </Text>
              )}
            </View>

            {(showUnarchiveCta ||
              showExtendCta ||
              needToBuy ||
              needContinue ||
              groupLive) && (
              <View style={tw`self-start`}>
                {showUnarchiveCta && (
                  <BtnBuyUnlock
                    text={t("Restore")}
                    onPress={() => openExternalUrl(unarchiveUrl)}
                  />
                )}
                {!showUnarchiveCta && showExtendCta && (
                  <BtnBuyUnlock
                    text={t("Extend")}
                    onPress={() => openExternalUrl(extendLinkUrl)}
                  />
                )}
                {!showUnarchiveCta && needToBuy && (
                  <BtnBuyUnlock
                    text={
                      memberView || !hasEnoughCredits
                        ? t("Unlock")
                        : account_type === "business"
                        ? t("Use credit")
                        : t("Use prepaid event")
                    }
                    onPress={async () => {
                      if (memberView) {
                        // An admin: the account's credits; the server says
                        // when there aren't enough.
                        setloadingUnlock(true);
                        try {
                          await unlockWithCredit(
                            slug,
                            daysToPay > 0 ? daysToPay : 1,
                            {
                              throwOnError: true,
                            }
                          );
                          await refetchProfile();
                          notify.success(t("Event unlocked"));
                        } catch (err) {
                          notify.error(
                            "Couldn't unlock this event",
                            teamsErrorMessage(
                              err,
                              `${team.name}'s account may not have enough credits. Ask the account holder to add more.`
                            )
                          );
                        }
                        setloadingUnlock(false);
                        return;
                      }
                      if (hasEnoughCredits) {
                        setloadingUnlock(true);
                        try {
                          await unlockWithCredit(slug, days - days_paid);
                          await refetchProfile();
                        } catch (err) {
                          Toast.show({
                            text: t("Could not unlock the event"),
                            type: "error",
                          });
                        }
                        setloadingUnlock(false);
                      } else {
                        if (account_type === "business") {
                          navigation.navigate("Plans", {
                            source: "event_paywall",
                            event,
                            slug,
                          });
                        } else {
                          navigation.navigate("PayWall", {
                            event,
                            slug,
                          });
                        }
                      }
                    }}
                    loading={loadingUnlock}
                  />
                )}
                {!showUnarchiveCta && needContinue && (
                  <TouchableOpacity
                    style={[
                      tw`rounded h-[42px] flex-row items-center justify-center`,
                      { backgroundColor: brandColor },
                    ]}
                    onPress={() => onGoLivePressed(event)}
                  >
                    <Ionicons
                      name="videocam-outline"
                      size={24}
                      color="white"
                      style={tw`ml-5`}
                    />
                    <Text style={tw`pl-2 pr-5 text-white text-base`}>
                      {liveButtonTitle}
                    </Text>
                  </TouchableOpacity>
                )}
                {!showUnarchiveCta &&
                  groupLive &&
                  (is_test ? <TestIndicator /> : <LiveIndicator />)}
              </View>
            )}
          </View>
        </View>
      </TouchableOpacity>
    );
  }
);

export default EventCard;
